<script>
import Layout from "@/router/layouts/main";
import { api } from "@/state/services";

export default {
  locales: {
    pt: {},
    es: {},
  },
  components: {
    Layout,
  },
  data() {
    return {
      poll: null,
      loading: true,
      notFound: false,
      selectedOptions: [],
      votedOptionIds: [],
      voting: false,
      voted: false,
      voteError: null,
    };
  },
  computed: {
    isActive() {
      if (!this.poll) return false;
      const now   = new Date();
      const start = new Date(this.poll.date_start);
      const end   = new Date(this.poll.date_end);
      return now >= start && now <= end;
    },
    canVote() {
      return this.isActive && !this.poll.already_voted && !this.voted;
    },
    showResults() {
      if (!this.poll) return false;
      const sr = this.poll.show_results;
      if (sr === "always") return true;
      if (sr === "after_vote" && (this.poll.already_voted || this.voted)) return true;
      return false;
    },
    totalVotes() {
      return this.poll ? (this.poll.total_votes || 0) : 0;
    },
  },
  methods: {
    load() {
      const id = this.$route.params.id;
      this.loading = true;

      api.get(`polls/${id}`).then((response) => {
        if (response.data.status === "success") {
          this.poll           = response.data.poll;
          this.votedOptionIds = (response.data.poll.voted_option_ids || []).map(Number);
          this.loading        = false;
        } else {
          this.notFound = true;
          this.loading  = false;
        }
      }).catch(() => {
        this.notFound = true;
        this.loading  = false;
      });
    },

    toggleOption(optId) {
      if (this.poll.type === "single") {
        this.selectedOptions = [optId];
      } else {
        const idx = this.selectedOptions.indexOf(optId);
        if (idx === -1) {
          this.selectedOptions.push(optId);
        } else {
          this.selectedOptions.splice(idx, 1);
        }
      }
    },

    isSelected(optId) {
      return this.selectedOptions.includes(optId);
    },

    vote() {
      if (!this.selectedOptions.length) {
        this.voteError = "Selecione ao menos uma opção.";
        return;
      }

      this.voteError = null;
      this.voting    = true;
      const id       = this.$route.params.id;

      api.post(`polls/${id}/vote`, { options: this.selectedOptions }).then((response) => {
        if (response.data.status === "success") {
          this.voted              = true;
          this.voting             = false;
          this.votedOptionIds     = (response.data.voted_option_ids || this.selectedOptions).map(Number);
          this.poll.options       = response.data.options;
          this.poll.total_votes   = response.data.total_votes;
          this.poll.show_results  = response.data.show_results;
        } else {
          this.voteError = response.data.message || "Erro ao registrar voto.";
          this.voting    = false;
        }
      }).catch((err) => {
        const msg = err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : "Erro ao registrar voto.";
        this.voteError = msg;
        this.voting    = false;
      });
    },

    getPercentage(votes) {
      if (!this.totalVotes) return 0;
      return Math.round((votes / this.totalVotes) * 100);
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<template>
  <Layout>
    <div class="page-title-box d-md-flex justify-content-between">
      <div>
        <h3>{{ t("Enquetes") }}</h3>
        <p></p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="card">
      <div class="card-body min-vh-25 d-flex justify-content-center align-items-center">
        <b-spinner variant="default" role="status"></b-spinner>
      </div>
    </div>

    <!-- Not found -->
    <div v-else-if="notFound" class="card">
      <div class="card-body min-vh-25 d-flex justify-content-center align-items-center">
        <div class="text-center">
          <p class="text-muted">Enquete não encontrada.</p>
          <router-link to="/polls" class="btn btn-primary btn-sm">Voltar às Enquetes</router-link>
        </div>
      </div>
    </div>

    <!-- Poll content -->
    <div v-else-if="poll">
      <div class="row justify-content-center">
        <div class="col-lg-7">
          <div class="card">
            <div class="card-body">

              <!-- Header -->
              <div class="d-flex justify-content-between align-items-start mb-3">
                <div class="d-flex align-items-center">
                  <span v-if="poll.already_voted || voted" class="badge badge-success mr-2 text-uppercase">Votado</span>
                  <span v-else-if="!isActive" class="badge badge-secondary mr-2 text-uppercase">Encerrado</span>
                  <span v-else class="badge badge-warning mr-2 text-white text-uppercase">Aguardando seu voto</span>
                  <span class="badge badge-light text-uppercase">
                    {{ poll.type === 'single' ? 'Seleção Única' : 'Múltipla Escolha' }}
                  </span>
                </div>
                <small class="text-muted">até {{ formatDate(poll.date_end) }}</small>
              </div>

              <h4 class="poll-view-title">{{ poll.title }}</h4>
              <p v-if="poll.description" class="text-muted mb-4" v-html="poll.description"></p>

              <!-- Alert: voted successfully -->
              <div v-if="voted" class="alert alert-success">
                Seu voto foi registrado com sucesso!
              </div>

              <!-- Alert: already voted (from API) -->
              <div v-else-if="poll.already_voted && !voted" class="alert alert-info">
                Você já votou nesta enquete.
              </div>

              <!-- Alert: not active -->
              <div v-else-if="!isActive" class="alert alert-secondary">
                Esta enquete não está mais disponível para votação.
              </div>

              <!-- Vote error -->
              <div v-if="voteError" class="alert alert-danger">{{ voteError }}</div>

              <!-- Options -->
              <div class="poll-options">
                <div
                  v-for="(opt, index) in poll.options"
                  :key="opt.id || index"
                  class="poll-option mb-3"
                  :class="{
                    'poll-option-selectable': canVote,
                    'poll-option-selected': canVote && isSelected(opt.id),
                    'poll-option-my-vote': !canVote && votedOptionIds.includes(opt.id),
                  }"
                  @click="canVote && toggleOption(opt.id)"
                >
                  <!-- Voting mode -->
                  <div v-if="canVote" class="d-flex align-items-center">
                    <div class="poll-checkbox mr-3" :class="{ 'poll-checkbox-active': isSelected(opt.id) }">
                      <svg v-if="isSelected(opt.id)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white">
                        <path d="m10 15.586-3.293-3.293-1.414 1.414L10 18.414l9.707-9.707-1.414-1.414z"></path>
                      </svg>
                    </div>
                    <span class="poll-option-text">{{ opt.text }}</span>
                  </div>

                  <!-- Results mode -->
                  <div v-else>
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <div class="d-flex align-items-center">
                        <span
                          v-if="votedOptionIds.includes(opt.id)"
                          class="poll-voted-mark mr-2"
                          title="Sua escolha"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="m10 15.586-3.293-3.293-1.414 1.414L10 18.414l9.707-9.707-1.414-1.414z"></path>
                          </svg>
                        </span>
                        <span class="poll-option-text" :class="{ 'poll-option-text-voted': votedOptionIds.includes(opt.id) }">{{ opt.text }}</span>
                      </div>
                      <span v-if="showResults" class="text-muted ml-2 text-nowrap">
                        <strong>{{ opt.votes || 0 }}</strong> — {{ getPercentage(opt.votes || 0) }}%
                      </span>
                    </div>
                    <div v-if="showResults" class="progress" style="height: 10px; border-radius: 5px;">
                      <div
                        class="progress-bar"
                        :class="{ 'progress-bar-voted': votedOptionIds.includes(opt.id) }"
                        :style="{ width: getPercentage(opt.votes || 0) + '%', transition: 'width 0.5s ease' }"
                        role="progressbar"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Vote button -->
              <div v-if="canVote" class="mt-4">
                <button
                  class="btn btn-primary btn-block"
                  :disabled="!selectedOptions.length || voting"
                  @click="vote"
                >
                  <b-spinner v-if="voting" small class="mr-2"></b-spinner>
                  {{ voting ? 'Enviando...' : 'Confirmar Voto' }}
                </button>
                <small class="text-muted d-block text-center mt-1">
                  {{ poll.type === 'single' ? 'Selecione uma opção' : 'Selecione uma ou mais opções' }}
                </small>
              </div>

              <!-- Total votes (results mode) -->
              <div v-if="showResults" class="text-center mt-3 text-muted">
                <small>{{ totalVotes }} voto(s) no total</small>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.poll-view-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
  line-height: 1.4;
}

.poll-option {
  padding: 12px 16px;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.15s, background 0.15s;
}

.poll-option-selectable {
  cursor: pointer;
}

.poll-option-selectable:hover {
  border-color: #556ee6;
  background: #f5f7ff;
}

.poll-option-selected {
  border-color: #556ee6;
  background: #f0f3ff;
}

.poll-checkbox {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.15s, background 0.15s;
}

.poll-checkbox-active {
  border-color: #556ee6;
  background: #556ee6;
}

.poll-option-text {
  font-size: 15px;
  font-weight: 500;
}

.progress-bar {
  background-color: #556ee6;
}

.progress-bar-voted {
  background-color: #34c38f;
}

.poll-option-my-vote {
  border-color: #34c38f;
  background: #f0faf6;
}

.poll-voted-mark {
  color: #34c38f;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.poll-option-text-voted {
  font-weight: 700;
}
</style>
